import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ComicCard from '@/components/base/ComicCard';
import { comics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const [comic, setComic] = useState<Comic | null>(null);
  const [related, setRelated] = useState<Comic[]>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  useEffect(() => {
    const found = comics.find((c) => c.id === Number(id));
    setComic(found || null);
    if (found) {
      const relatedComics = comics
        .filter((c) => c.category === found.category && c.id !== found.id)
        .slice(0, 4);
      setRelated(relatedComics);
    }
  }, [id]);

  if (!comic) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
        <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
          <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
        </div>
        <h2 className="text-lg font-heading font-bold text-foreground-700 mb-2">漫画未找到</h2>
        <p className="text-sm text-foreground-500 mb-6">您要查看的漫画不存在或已下架</p>
        <Link
          to="/category/all"
          className="inline-flex px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          返回漫画大全
        </Link>
      </div>
    );
  }

  const categoryMap: Record<string, string> = {
    hanman: '韩漫',
    riman: '日漫',
    guoman: '国漫',
  };

  const catName = categoryMap[comic.category] || comic.category;

  const title = `${comic.title} - ${comic.author} | 樱花漫画网`;
  const desc = `${comic.title}，${comic.author}作品，${comic.status}${catName}。${comic.description.substring(0, 100)}...樱花漫画网提供免费完整版下拉式在线阅读。`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - ${catName}`,
    author: { '@type': 'Person', name: comic.author },
    description: comic.description,
    datePublished: comic.chapters[0]?.updateDate || '',
    dateModified: comic.updateDate,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://yinghuacomic.com' },
      { '@type': 'ListItem', position: 2, name: catName, item: `https://yinghuacomic.com/category/${comic.category}` },
      { '@type': 'ListItem', position: 3, name: comic.title, item: `https://yinghuacomic.com/article/${comic.id}` },
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description: desc,
  };

  return (
    <>
      <SEO
        title={title}
        description={desc}
        keywords={`${comic.title},${catName},全集漫画,漫画全集,樱花漫画网,下拉式阅读,${comic.author}`}
        canonical={`https://yinghuacomic.com/article/${comic.id}`}
        ogTitle={title}
        ogDescription={desc}
        ogType="article"
        ogUrl={`https://yinghuacomic.com/article/${comic.id}`}
        twitterCard="summary_large_image"
        jsonLd={[articleSchema, breadcrumbSchema, webPageSchema]}
      />

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
        <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6 flex-wrap" aria-label="面包屑导航">
          <a href="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</a>
          <span>/</span>
          <a href={`/category/${comic.category}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">{catName}</a>
          <span>/</span>
          <span className="text-foreground-600">{comic.title}</span>
        </nav>

        <div className="flex flex-col md:flex-row gap-8 mb-10">
          <div className="flex-shrink-0">
            <div className="w-[200px] aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
              <img
                src={comic.cover}
                alt={comic.title}
                title={`${comic.title} - ${catName}封面`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              {comic.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-sm text-foreground-500">{comic.author}</span>
              <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap">
                {catName}
              </span>
              <span className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap ${
                comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-background-200 text-foreground-500'
              }`}>
                {comic.status}
              </span>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-xs text-accent-500"></i>
                </div>
                <span className="text-sm font-semibold text-foreground-700">{comic.rating}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-5">
              {comic.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded-full text-xs bg-background-100 text-foreground-500 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            <p className="text-sm text-foreground-600 leading-relaxed mb-5">
              {comic.description}
            </p>

            <div className="flex items-center gap-3 text-xs text-foreground-400">
              <span>更新至 {comic.chapters.length} 话</span>
              <span>|</span>
              <span>{comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views} 次阅读</span>
              <span>|</span>
              <span>最近更新：{comic.updateDate}</span>
            </div>
          </div>
        </div>

        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-5 rounded-full bg-primary-500"></div>
            <h2 className="text-lg font-heading font-bold text-foreground-900">
              章节目录
            </h2>
            <span className="text-sm text-foreground-400">
              （共{comic.chapters.length}话）
            </span>
          </div>
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-background-200/50">
              {comic.chapters.map((ch) => (
                <div
                  key={ch.id}
                  className="flex items-center justify-between px-4 py-3 bg-background-50 hover:bg-background-100/50 transition-colors cursor-pointer"
                >
                  <span className="text-sm text-foreground-700 truncate">{ch.title}</span>
                  <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{ch.updateDate}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-3">
          <div className="w-8 h-px bg-background-300"></div>
          <div className="w-6 h-6 flex items-center justify-center">
            <i className="ri-book-2-line text-sm text-primary-500"></i>
          </div>
          <span className="text-sm text-foreground-500">— 下拉式阅读，畅享漫画全集 —</span>
          <div className="w-6 h-6 flex items-center justify-center">
            <i className="ri-book-2-line text-sm text-primary-500"></i>
          </div>
          <div className="w-8 h-px bg-background-300"></div>
        </div>

        {related.length > 0 && (
          <div className="mt-12">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-1 h-5 rounded-full bg-accent-500"></div>
              <h2 className="text-lg font-heading font-bold text-foreground-900">
                相关推荐
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-product-shop>
              {related.map((r) => (
                <ComicCard key={r.id} comic={r} size="large" />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}